import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, setUnauthorizedHandler } from './api';

type AuthState = 'checking' | 'anon' | 'authed';

const Ctx = createContext<{ state: AuthState; login: (pin: string) => Promise<void> }>({
  state: 'checking',
  login: async () => {},
});

// The session check must never be able to hang the app: each attempt is
// cut off after 4s, it retries once, and any failure lands on the PIN gate
// (which a valid cookie sails through on the next successful request).
async function checkSession(): Promise<boolean> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    try {
      const res = await fetch('/api/session', { signal: ctrl.signal });
      clearTimeout(timer);
      if (res.ok) {
        const data = (await res.json()) as { authed?: boolean };
        return data.authed === true;
      }
      return false; // a real answer (401 etc.) — no retry needed
    } catch {
      clearTimeout(timer); // timeout or network blip — retry once
    }
  }
  return false;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>('checking');

  useEffect(() => {
    setUnauthorizedHandler(() => setState('anon'));
    checkSession().then((ok) => setState(ok ? 'authed' : 'anon'));
  }, []);

  async function login(pin: string) {
    await api.login(pin); // throws on a wrong PIN or rate limit
    setState('authed');
  }

  return <Ctx.Provider value={{ state, login }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  return useContext(Ctx);
}
